import { useFormikContext } from 'formik';

import { getDocumentName, ResolvableModeEnum } from '@e-socna/data-layer';
import { invariant } from '@e-socna/utils';

import { MissionPage_OneMission_QueryDocument } from '../../pages/Mission/hooks/useFindOneMissionQuery.hook';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import { ResolvableServiceUserFormStep } from '../ResolvableServiceUser';
import type { Props } from '../ResolvableServiceUser/types/props.type';
import type { MissionForm_Mission } from '../Mission/fragments/mission.fragment';

import { useCreateMissionResolvableServiceUsageMutation } from './hooks/useCreateMissionResolvableServiceUsageMutation.hook';
import { useCustomizeAllMissionResolvableServiceUsagesMutation } from './hooks/useCustomizeAllMissionResolvableServiceUsagesMutation.hook';
import { useFlushAllMissionResolvableServiceUsagesMutation } from './hooks/useFlushAllMissionResolvableServiceUsagesMutation.hook';
import { useRemoveMissionResolvableServiceUsageMutation } from './hooks/useRemoveMissionResolvableServiceUsageMutation.hook';
import { useUpdateMissionResolvableServiceUsageMutation } from './hooks/useUpdateMissionResolvableServiceUsageMutation.hook';
import { useCreateMissionServiceUsagesGroupMutation } from './hooks/useCreateMissionServiceUsagesGroupMutation.hook';
import { useRemoveMissionServiceUsagesGroupMutation } from './hooks/useRemoveMissionServiceUsagesGroupMutation.hook';
import { useUpdateMissionServiceUsagesGroupMutation } from './hooks/useUpdateMissionServiceUsagesGroupMutation.hook';
import { useReorderMissionServiceUsagesGroupsMutation } from './hooks/useReorderMissionServiceUsagesGroupsMutation.hook';
import { useSwitchMissionGroupingModeMutation } from './hooks/useSwitchMissionGroupingModeMutation.hook';
import { useMoveMissionServiceUsageToGroupMutation } from './hooks/useMoveMissionServiceUsageToGroupMutation.hook';
import { useSwitchMissionTotalModeMutation } from './hooks/useSwitchMissionTotalModeMutation.hook';
import { useUpdateMissionManualTotalMutation } from './hooks/useUpdateMissionManualTotalMutation.hook';

export const MissionResolvableServiceUsagesFormStep = ({
  additionalTotalGroups,
  fallbackResolvableServiceUsagesGroups,
  groupSectionLabel,
  groupTotalCalculations,
  id,
  leadingContent,
  optionGroupsSelection,
  paths,
  selectedOptionGroupIds,
  totalCalculation,
  totalSections,
  useDirectMutation,
}: Omit<
  Props,
  | 'documentResolversSettings'
  | 'storage'
  | 'mutations'
  | 'queryKeysToInvalidate'
  | 'resolvableFieldSettings'
>) => {
  const { fileDirectoryCategoriesCodeMap, missionFileCategoriesCodeMap } =
    useReferencesContext();

  const { values } = useFormikContext<MissionForm_Mission>();

  const { mutate: createResolvableServiceUsageMutation } =
    useCreateMissionResolvableServiceUsageMutation();
  const { mutate: customizeAllResolvableServiceUsagesMutation } =
    useCustomizeAllMissionResolvableServiceUsagesMutation();
  const { mutate: flushAllResolvableServiceUsagesMutation } =
    useFlushAllMissionResolvableServiceUsagesMutation();
  const { mutate: removeResolvableServiceUsageMutation } =
    useRemoveMissionResolvableServiceUsageMutation();
  const { mutate: updateResolvableServiceUsageMutation } =
    useUpdateMissionResolvableServiceUsageMutation();
  const { mutate: switchGroupingModeMutation } =
    useSwitchMissionGroupingModeMutation();
  const { mutate: moveToGroupMutation } =
    useMoveMissionServiceUsageToGroupMutation();
  const { mutate: switchTotalModeMutation } =
    useSwitchMissionTotalModeMutation();
  const { mutate: updateManualTotalMutation } =
    useUpdateMissionManualTotalMutation();

  const { mutate: createGroupMutation } =
    useCreateMissionServiceUsagesGroupMutation();
  const { mutate: removeGroupMutation } =
    useRemoveMissionServiceUsagesGroupMutation();
  const { mutate: updateGroupMutation } =
    useUpdateMissionServiceUsagesGroupMutation();
  const { mutate: reorderGroupsMutation } =
    useReorderMissionServiceUsagesGroupsMutation();

  const mutations = {
    groups: {
      createGroup: createGroupMutation,
      removeGroup: removeGroupMutation,
      reorderGroups: reorderGroupsMutation,
      updateGroup: updateGroupMutation,
    },
    create: createResolvableServiceUsageMutation,
    flushAll: flushAllResolvableServiceUsagesMutation,
    moveToGroup: moveToGroupMutation,
    customizeAll: customizeAllResolvableServiceUsagesMutation,
    remove: removeResolvableServiceUsageMutation,
    switchGroupingMode: switchGroupingModeMutation,
    switchTotalMode: switchTotalModeMutation,
    update: updateResolvableServiceUsageMutation,
    updateManualTotal: updateManualTotalMutation,
  };

  invariant(
    !!(useDirectMutation && totalCalculation),
    "If you want to use this form with a client state (!directMutation, or Creation variant) and / or without supplied totalCalculation, you'll have to implement it yourself. You can take as example the resolvable mission type form. You'll also have to create calculateMissionTotal query on the backend side.",
  );

  return (
    <ResolvableServiceUserFormStep
      additionalTotalGroups={additionalTotalGroups}
      documentResolversSettings={{
        allowedReturnTypeIds: undefined,
        enableDirectGeneration: true,
        mustBeResolvableOnBack: false,
      }}
      fallbackResolvableServiceUsagesGroups={
        fallbackResolvableServiceUsagesGroups
      }
      formContext="servicesInfo"
      groupSectionLabel={groupSectionLabel}
      groupTotalCalculations={groupTotalCalculations}
      id={id}
      leadingContent={leadingContent}
      mutations={mutations}
      optionGroupsSelection={optionGroupsSelection}
      paths={paths}
      queryKeysToInvalidate={
        [
          [
            getDocumentName(MissionPage_OneMission_QueryDocument),
            { id: values._id },
          ],
        ] as readonly (readonly string[])[]
      }
      resolvableFieldSettings={{
        enableDirectGeneration: true,
        primaryMode: ResolvableModeEnum.Static,
      }}
      selectedOptionGroupIds={selectedOptionGroupIds}
      storage={{
        directoryCategoryId: fileDirectoryCategoriesCodeMap.Mission._id,
        entityId: values._id,
        imageCategory: missionFileCategoriesCodeMap.TextEditorImages,
      }}
      totalCalculation={totalCalculation}
      totalSections={totalSections}
      useDirectMutation={useDirectMutation}
    />
  );
};
